function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useCallback, useMemo, useState } from 'react';
import { createSearchTextFilter, createSelectedValuesFilter } from '@deephaven/jsapi-utils';
import { useDebouncedCallback, useDebouncedValue, usePromiseFactory } from '@deephaven/react-hooks';
import { usePickerItemScale } from '@deephaven/components';
import useFilterConditionFactories from "./useFilterConditionFactories.js";
import useViewportData from "./useViewportData.js";
import useViewportFilter from "./useViewportFilter.js";
import useTableUtils from "./useTableUtils.js";
import useWidgetClose from "./useWidgetClose.js";
import { SEARCH_DEBOUNCE_MS, VIEWPORT_PADDING, VIEWPORT_SIZE } from "./Constants.js";
/**
 * Manages a list of available items that can be searched and selected. Selected
 * items are removed from the list and managed in a selectedValueMap data
 * structure. Useful for components that contain a picker but show selected
 * values in a separate component.
 * @param maybeTable The table to get the list of items from
 * @param columnName The column name to get the list of items from
 * @param mapItemToValue A function to map an item to a value
 * @param filterConditionFactories Optional filter condition factories to apply to the list
 * @param trimSearchText Whether to trim the search text before filtering. Defaults to false
 * @param timeZone The timezone to use for date parsing
 */
export function usePickerWithSelectedValues(_ref) {
  var maybeTable = _ref.maybeTable,
    columnName = _ref.columnName,
    mapItemToValue = _ref.mapItemToValue,
    _ref$filterConditionF = _ref.filterConditionFactories,
    filterConditionFactories = _ref$filterConditionF === void 0 ? [] : _ref$filterConditionF,
    _ref$trimSearchText = _ref.trimSearchText,
    trimSearchText = _ref$trimSearchText === void 0 ? false : _ref$trimSearchText,
    timeZone = _ref.timeZone;
  var _usePickerItemScale = usePickerItemScale(),
    itemHeight = _usePickerItemScale.itemHeight;
  var tableUtils = useTableUtils();

  // `searchText` should always be up to date for controlled search input.
  // `appliedSearchText` will get updated after a delay to avoid updating
  // filters on every key stroke. It will also be trimmed of leading / trailing
  // spaces if `trimSearchText` is true.
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchText = _useState2[0],
    setSearchText = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    appliedSearchText = _useState4[0],
    setAppliedSearchText = _useState4[1];
  var applySearchText = useCallback(text => {
    setAppliedSearchText(trimSearchText ? text.trim() : text);
  }, [trimSearchText]);
  var searchTextMaybeTrimmed = useMemo(() => trimSearchText ? searchText.trim() : searchText, [searchText, trimSearchText]);
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedKey = _useState6[0],
    setSelectedKey = _useState6[1];
  var _useState7 = useState(() => new Map()),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedValueMap = _useState8[0],
    setSelectedValueMap = _useState8[1];
  var _usePromiseFactory = usePromiseFactory(tableUtils.doesColumnValueExist, [maybeTable, columnName, appliedSearchText, false /* isCaseSensitive */]),
    valueExists = _usePromiseFactory.data,
    valueExistsIsLoading = _usePromiseFactory.isLoading;

  // The `searchTextFilter` starts getting applied to the list whenever
  // `appliedSearchText` changes, after which there is a small delay before the
  // items are in sync. Use a debounce timer to allow a little extra time
  // before calculating `searchTextExists` below. Note that there are 2 debounce
  // timers at play here:
  // 1. `onDebouncedSearchTextChange` applies the search text after user stops typing
  // 2. `useDebouncedValue` debounces whenever the result of the first debounce
  //    changes, and `isApplyingFilter` will be true while this 2nd timer is active.
  var _useDebouncedValue = useDebouncedValue(appliedSearchText, SEARCH_DEBOUNCE_MS),
    isApplyingFilter = _useDebouncedValue.isDebouncing;

  // If value exists check is still loading or if debounce hasn't completed, set
  // `searchTextExists` to null since it is indeterminate.
  var searchTextExists = isApplyingFilter || valueExistsIsLoading ? null : valueExists;
  var searchTextFilter = useMemo(() => createSearchTextFilter(tableUtils, columnName, appliedSearchText, timeZone), [appliedSearchText, columnName, tableUtils, timeZone]);

  // Filter out selected values from the picker
  var excludeSelectedValuesFilter = useMemo(() => createSelectedValuesFilter(tableUtils, columnName, new Set(selectedValueMap.keys()), false, true), [columnName, selectedValueMap, tableUtils]);
  var _usePromiseFactory2 = usePromiseFactory(tableUtils.createDistinctSortedColumnTable, [maybeTable, columnName, 'asc', ...filterConditionFactories]),
    listTable = _usePromiseFactory2.data;
  useWidgetClose(listTable);
  var list = useViewportData({
    table: listTable,
    itemHeight,
    viewportSize: VIEWPORT_SIZE,
    viewportPadding: VIEWPORT_PADDING
  });
  var hasSearchTextWithZeroResults = searchTextMaybeTrimmed.length > 0 && list.size === 0;
  var searchTextIsInSelectedValues = selectedValueMap.has(searchTextMaybeTrimmed);
  var onDebouncedSearchTextChange = useDebouncedCallback(applySearchText, SEARCH_DEBOUNCE_MS);
  var onSearchTextChange = useCallback(text => {
    setSearchText(text);
    onDebouncedSearchTextChange(text);
  }, [onDebouncedSearchTextChange]);
  var setSelectedKeyOnNextFrame = useDebouncedCallback(setSelectedKey, 0);
  var onSelectKey = useCallback(key => {
    setSearchText('');
    applySearchText('');

    // Set the selection temporarily to avoid the picker staying open
    setSelectedKey(key);

    // Clear the selection on next frame since selected items get removed from
    // the list and added to `selectedValues` Map
    setSelectedKeyOnNextFrame(null);

    // key will be null in scenarios where search text doesn't match an item
    // and user clicks outside of picker
    if (key == null) {
      return;
    }
    var item = list.viewportData.findItem(key);
    if (item == null) {
      return;
    }
    var value = mapItemToValue(item);
    setSelectedValueMap(prev => {
      var next = new Map(prev);
      next.set(value, {
        value
      });
      return next;
    });
  }, [applySearchText, setSelectedKeyOnNextFrame, list.viewportData, mapItemToValue]);
  var onAddValues = useCallback(values => {
    setSelectedValueMap(prev => {
      if (values.size === 0) {
        return prev;
      }
      var next = new Map(prev);

      // eslint-disable-next-line no-restricted-syntax
      for (var value of values) {
        next.set(value, {
          value
        });
      }
      return next;
    });
  }, []);
  var onRemoveValues = useCallback(values => {
    setSelectedValueMap(prev => {
      if (values === 'all') {
        return new Map();
      }
      var next = new Map(prev);

      // eslint-disable-next-line no-restricted-syntax
      for (var value of values) {
        next.delete(value);
      }
      return next;
    });
  }, []);
  var filter = useFilterConditionFactories(list.table, searchTextFilter, excludeSelectedValuesFilter);
  useViewportFilter(list, filter);
  return useMemo(() => ({
    list,
    hasSearchTextWithZeroResults,
    searchText,
    searchTextExists,
    searchTextIsInSelectedValues,
    selectedKey,
    selectedValueMap,
    onSearchTextChange,
    onSelectKey,
    onAddValues,
    onRemoveValues
  }), [hasSearchTextWithZeroResults, list, onAddValues, onRemoveValues, onSearchTextChange, onSelectKey, searchText, searchTextExists, searchTextIsInSelectedValues, selectedKey, selectedValueMap]);
}
export default usePickerWithSelectedValues;
//# sourceMappingURL=usePickerWithSelectedValues.js.map